<template>
  <div class="instagram-instantwin-prize" v-loading="!firstLoaded">
    <div class="mb-x2" layout="layout" layout-align="space-between-center">
      <div layout="layout">
        <div class="mr-x1">
          <el-button class="el-button-mt" type="default" @click="onChangeBackEvent">
            <i class="mdi mdi-arrow-left"></i>{{ '戻る' }}
          </el-button>
        </div>
      </div>
      <div v-if="campaign && campaign.id" layout="row" layout-align="end-center">
        <div class="mr-x3">{{ '応募者数' }}: {{ formatNumber(totalApplicantCount) }}</div>
        <div class="mr-x3">
          {{ '当選者数' }}: {{ formatNumber(campaign.winnerCount) }}({{ '残り'
          }}{{ formatNumber(campaign.maxWinnerCount - campaign.winnerCount) }})
        </div>
      </div>
    </div>

    <div v-if="isCampaignBeforeStart">
      <div class="mb-x2">
        <div class="message message-info">
          このキャンペーンは、{{ campaignStartDatetime }}から開催されます。<br />
          <template v-if="campaign && campaign.setting && campaign.setting.entryActionType === 'mention'">
            応募収集は、リアルタイムで収集されます。
          </template>
        </div>
      </div>
    </div>
    <div v-else-if="isCampaignBeforeCollect">
      <div class="mb-x2">
        <div class="message message-info">
          <template v-if="campaign && campaign.setting && campaign.setting.entryActionType === 'mention'">
            応募収集は、リアルタイムで収集されます。
          </template>
        </div>
      </div>
    </div>
    <div v-else-if="isCampaignFinalCollection">
      <div class="mb-x2">
        <div class="message message-info">
          現在、応募データの最終集計処理を行なっています。<br />{{
            collectCompletedAt
          }}ごろまでに全応募データが反映されます。応募件数により、反映が遅れる場合があります。
        </div>
      </div>
    </div>
    <div v-else-if="isCampaignCollectionCompleted">
      <div class="mb-x2">
        <div class="message message-info">全応募データの最終集計が完了しました。</div>
      </div>
    </div>

    <el-card v-if="!error">
      <div class="mb-x3" layout="row" layout-align="space-between-center">
        <div class="header-left" layout="row">
          <el-button
            v-if="canCreatePrize"
            type="primary"
            :disabled="isCreatePrizeDisabled"
            @click="onOpenCreatePrizeDialog"
          >
            <i class="mdi mdi-plus"></i>応募グループの新規作成
          </el-button>
        </div>
        <div class="header-right" layout="row">
          <CsvDownload v-if="!error && prizes.length" :download="downloadCsv" />
        </div>
      </div>
      <div class="message message-error" v-if="firstLoaded && !prizes.length">
        {{ 'キャンペーン開始日時までに応募グループを作成してください。' }}<br />
        {{ '応募グループがない場合、データを取得できません。' }}
      </div>
      <InstagramInstantwinPrizeTable
        ref="prizeTableRef"
        v-show="prizes.length"
        :prizes="prizes"
        :campaign-id="campaign ? campaign.id : 0"
        :campaign-status="campaign ? campaign.status : undefined"
        :has-running-campaign-on-same-account="hasRunningCampaignOnSameAccount"
        :can-edit-campaign="canCreatePrize"
        :options="options"
        :onChangePagingNo="onChangePagingNo"
        :onChangeLimitCount="onChangeLimitCount"
        :onChangeSortBy="onChangeSortBy"
        :onOpenApplicant="onOpenApplicant"
        :onRefresh="onRefresh"
      />
    </el-card>
    <el-card v-else>
      <div class="message message-error">
        {{ 'データ取得に失敗しました。恐れ入りますが、時間を置いて再度お試しください。' }}
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'

import CsvDownload from '@/client/components/_atoms/csv_download/csv_download.vue'
import { CAMPAIGN_STATUS } from '@/client/constant'
import api from '@/client/core/api'
import util from '@/client/core/util'
import { formatNumber } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import { useNavbarStore } from '@/client/store/useNavbarStore'
import { GetInstagramInstantwinPrizesData, TPrize } from '@/typings/api/instagram_instantwin_prizes.schema'

import { useInstagramInstantwinPrizeStore } from '../hooks/instagramInstantwinPrizeStore'
import { buildInstantwinPrizeKeywordText } from '../utils/prizeKeyword'
import InstagramInstantwinPrizeTable from './InstagramInstantwinPrizeTable.vue'

// Vue 2.7のComposition API でVueインスタンスを取得
const instance = getCurrentInstance()
const $route = instance?.proxy.$route
const $router = instance?.proxy.$router
const navbarStore = useNavbarStore()

const store = useInstagramInstantwinPrizeStore()
const prizeTableRef = ref<{ openCreateDialog: () => void } | null>(null)

const { prizes, error, campaign, firstLoaded, options, params, totalApplicantCount, hasRunningCampaignOnSameAccount } =
  storeToRefs(store)

// computed properties
const campaignStartDatetime = computed(() => {
  if (!campaign.value?.startDatetime) {
    return '-'
  }
  return moment(campaign.value.startDatetime).format('YYYY年MM月DD日 HH:mm')
})

const collectCompletedAt = computed(() => {
  if (!campaign.value?.endDatetime) {
    return '-'
  }
  return moment(campaign.value.endDatetime).add(1, 'hours').format('YYYY年MM月DD日 HH:mm')
})

const isCampaignBeforeStart = computed(() => campaign.value?.status === CAMPAIGN_STATUS.BEFORE_EVENT)
const isCampaignBeforeCollect = computed(() => campaign.value?.status === CAMPAIGN_STATUS.BEFORE_COLLECTION)
const isCampaignFinalCollection = computed(() => campaign.value?.status === CAMPAIGN_STATUS.DURING_FINAL_COLLECTION)
const isCampaignCollectionCompleted = computed(() => campaign.value?.status === CAMPAIGN_STATUS.COLLECTION_COMPLETED)
const isCreatePrizeDisabled = computed(() => !isCampaignBeforeStart.value)
const canCreatePrize = computed(() => Boolean(campaign.value?.id))

// methods
const onChangePagingNo = (page: number) => {
  if (params.value.pagingNo === page) {
    return
  }
  store.setParams({ pagingNo: page })
  util.scrollToTop()
}

const onChangeLimitCount = (limitCount: number) => {
  if (params.value.limitCount === limitCount) {
    return
  }
  store.setParams({ limitCount })
}

const onChangeSortBy = (sortBy: string) => {
  if (params.value.sortBy === sortBy) {
    return
  }
  store.setParams({ sortBy })
}

const onChangeBackEvent = () => {
  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|戻る')
  $router?.push({ path: '/campaigns' })
}

const onOpenApplicant = (prizeId: number) => {
  $router?.push(`/instagram_instantwins/${campaign.value?.id}/prizes/${prizeId}/applicants`)
}

const onOpenCreatePrizeDialog = () => {
  if (!canCreatePrize.value || isCreatePrizeDisabled.value) {
    return
  }

  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|応募グループの新規作成')
  prizeTableRef.value?.openCreateDialog()
}

const onRefresh = async () => {
  await store.fetchPrizes()
  await store.fetchCampaign()
  setBreadcrumb()
}

const setBreadcrumb = () => {
  if (!campaign.value?.id) {
    return
  }

  navbarStore.setBreadcrumb([
    {
      name: 'キャンペーン一覧',
      link: '/campaigns'
    },
    {
      name: campaign.value.title,
      link: null,
      iconName: campaign.value.account ? campaign.value.account.name : '削除されたアカウント',
      iconUrl: campaign.value.account ? campaign.value.account.pictureUrl : null,
      iconType: campaign.value.snsType,
      isIcon: true,
      label: 'インスタントウィン'
    }
  ])
}

/**
 * CSVダウンロード
 */
const downloadCsv = async () => {
  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|CSVダウンロード')

  const csvParams = Object.assign({}, params.value, {
    forCsv: 1
  })

  const result = await api.get<GetInstagramInstantwinPrizesData>('/instagram_instantwin_prizes', {
    params: csvParams
  })

  if (!result.data) {
    return {
      error: 'ERROR_LOADING'
    }
  }

  const prizesData = result.data.prizes
  const filename = `instagram_instantwin_prize_${campaign.value?.title}_applicants_${moment().format('YYYY_MM_DD')}`
  const csvData: (string | number)[][] = []
  csvData.push(['応募グループ名', '応募キーワード', '応募者数', '当選者数', '当選者数(残り)'])

  // CSVデータを格納
  prizesData.forEach((prize: TPrize) => {
    const keyword = buildInstantwinPrizeKeywordText({
      prizeEntryKeyword: prize.entryKeyword
    })

    csvData.push([
      prize.name,
      keyword,
      prize.applicantCount,
      prize.winnerCount,
      prize.maxWinnerCount - prize.winnerCount
    ])
  })

  return {
    filename,
    data: csvData
  }
}

// lifecycle
onMounted(async () => {
  if (!$route?.params.campaignId) {
    return
  }

  const campaignId = parseInt($route.params.campaignId as string)

  store.setParams({
    campaignId: campaignId
  })

  await store.fetchCampaign()

  if (!campaign.value || !campaign.value.id) {
    return
  }

  setBreadcrumb()

  // 初回データ取得
  await store.fetchPrizes()
})

// パラメータの変更を監視
watch(
  () => params.value,
  () => {
    store.fetchPrizes()
  },
  { deep: true }
)
</script>

<style lang="scss" scoped>
.instagram-instantwin-prize {
  .header-left {
    display: flex;
    align-items: center;
  }

  .header-right {
    margin-left: auto;
  }

  .message {
    padding: 16px;
    text-align: left;

    &.message-error {
      color: #f56c6c;
      border: 1px solid #fbc4c4;
      border-radius: 4px;
    }
  }
}
</style>
